import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/images/Inspect IQ Logo.svg';
import { trackCta } from '../utils/analytics';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Services', path: '/services' },
    { text: 'Platform', path: '/platform' },
    { text: 'About Us', path: '/about' },
    { text: 'Careers', path: '/careers' },
    { text: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 280, pt: 2, bgcolor: 'background.paper' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          px: 2,
          mb: 2
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="InspectIQ Logo"
          sx={{
            height: 40,
            mr: 1
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: 'primary.main',
            fontWeight: 700
          }}
        >
          InspectIQ
        </Typography>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
            sx={{
              py: 2,
              '&.Mui-selected': {
                backgroundColor: 'rgba(1, 86, 122, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(1, 86, 122, 0.12)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(1, 86, 122, 0.04)',
              }
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 600 : 400,
                color: 'primary.main',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'background.paper',
          color: 'primary.main',
          boxShadow: '0px 1px 10px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer' 
                }}
                onClick={() => handleNavigation('/')}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="InspectIQ Logo"
                  sx={{
                    height: 40,
                    mr: 1
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    display: 'block' // Always show the text
                  }}
                >
                  InspectIQ
                </Typography>
              </Box>
              {!isMobile && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    ml: 2,
                    color: 'text.secondary',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    pl: 2,
                  }}
                >
                  Expert inspections, Trusted results
                </Typography>
              )}
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(1, 86, 122, 0.08)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {menuItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Button
                      key={item.text}
                      onClick={() => handleNavigation(item.path)}
                      disableRipple
                      sx={{
                        fontWeight: active ? 600 : 500,
                        color: active ? 'primary.main' : 'text.primary',
                        position: 'relative',
                        px: 1.75,
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'transparent', color: 'primary.main' },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 14,
                          right: 14,
                          bottom: 6,
                          height: 2,
                          borderRadius: 2,
                          backgroundColor: 'secondary.main',
                          transform: active ? 'scaleX(1)' : 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.25s ease',
                        },
                        '&:hover::after': { transform: 'scaleX(1)' },
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
                <Button
                  variant="contained"
                  onClick={() => {
                    trackCta('Get a Quote', 'navbar');
                    handleNavigation('/quote');
                  }}
                  sx={{ ml: 1.5, px: 2.5 }}
                >
                  Get a Quote
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            borderLeft: 'none',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;